import React, { useMemo, useState, useCallback, useRef, useEffect } from "react";
import { Card } from "react-bootstrap";
import { GridData } from "../types";
import GridCell from "./GridCell";
import PaginationControls from "./PaginationControls";
import ChartModal, { ChartInfo } from "./ChartModal";
import { getChartPalette } from "../../../theme/chartTheme";

export interface CellClickInfo {
  xValue: number | string;
  yValue: number | string;
  xLabel: string;
  yLabel: string;
  legendIndex?: number;
}

interface PivotGridProps {
  data: GridData;
  onPageChange: (xPage: number, yPage: number) => void;
  onCellClick?: (info: CellClickInfo) => void;
  onInterpret?: () => void;
  onExport?: () => void;
  exporting?: boolean;
}

const PivotGrid: React.FC<PivotGridProps> = ({ data, onPageChange, onCellClick, onInterpret, onExport, exporting }) => {
  const {
    legends,
    xLabels,
    yLabels,
    datasets,
    totals,
    pagination,
  } = data;

  const legendCount = legends.length;
  const isPaginated = pagination.yTotal > pagination.yPageSize;

  const [chartInfo, setChartInfo] = useState<ChartInfo | null>(null);

  // Get cell values for a specific y row across all legends
  const getCellValues = (yIdx: number, xIdx: number): number[] => {
    return datasets.map((legendMatrix) => {
      const row = legendMatrix[yIdx];
      return row ? (row[xIdx] ?? 0) : 0;
    });
  };

  // Get row total values across all legends
  const getRowTotalValues = (yIdx: number): number[] => {
    return totals.rowTotals.map((legendTotals) => legendTotals[yIdx] ?? 0);
  };

  // Get column total values across all legends (grand total across ALL rows)
  const getColTotalValues = (xIdx: number): number[] => {
    return totals.columnTotals.map(
      (legendTotals) => legendTotals[xIdx] ?? 0
    );
  };

  // Page subtotals: sum of visible rows for each column (per legend)
  const pageColSubtotals = useMemo((): number[][] => {
    return datasets.map((legendMatrix) => {
      return xLabels.map((_, xIdx) => {
        let sum = 0;
        for (let yIdx = 0; yIdx < yLabels.length; yIdx++) {
          const row = legendMatrix[yIdx];
          sum += row ? (row[xIdx] ?? 0) : 0;
        }
        return sum;
      });
    });
  }, [datasets, xLabels, yLabels]);

  // Page grand subtotal: sum of visible row totals (per legend)
  const pageGrandSubtotal = useMemo((): number[] => {
    return totals.rowTotals.map((legendTotals) => {
      let sum = 0;
      for (let yIdx = 0; yIdx < yLabels.length; yIdx++) {
        sum += legendTotals[yIdx] ?? 0;
      }
      return sum;
    });
  }, [totals.rowTotals, yLabels]);

  const handleCellClick = (xIdx: number, yIdx: number, legendIndex?: number) => {
    if (!onCellClick) return;
    const xLbl = xLabels[xIdx];
    const yLbl = yLabels[yIdx];
    if (!xLbl || !yLbl) return;
    onCellClick({
      xValue: xLbl.id,
      yValue: yLbl.id,
      xLabel: xLbl.name,
      yLabel: yLbl.name,
      legendIndex,
    });
  };

  // Chart for a row: show values across all X columns
  const handleRowChart = useCallback(
    (yIdx: number) => {
      const yLabel = yLabels[yIdx];
      if (!yLabel) return;
      const palette = getChartPalette(6); // resolved at click time — follows theme/mode
      setChartInfo({
        title: yLabel.name,
        labels: xLabels.map((x) => x.name),
        datasets: legends.map((legend, lIdx) => ({
          legend,
          values: xLabels.map((_, xIdx) => {
            const row = datasets[lIdx]?.[yIdx];
            return row ? (row[xIdx] ?? 0) : 0;
          }),
          color: palette[lIdx % palette.length],
        })),
      });
    },
    [xLabels, yLabels, datasets, legends]
  );

  // Chart for a column: show values across all visible Y rows
  const handleColChart = useCallback(
    (xIdx: number) => {
      const xLabel = xLabels[xIdx];
      if (!xLabel) return;
      const palette = getChartPalette(6); // resolved at click time — follows theme/mode
      setChartInfo({
        title: xLabel.name,
        labels: yLabels.map((y) => y.name),
        datasets: legends.map((legend, lIdx) => ({
          legend,
          values: yLabels.map((_, yIdx) => {
            const row = datasets[lIdx]?.[yIdx];
            return row ? (row[xIdx] ?? 0) : 0;
          }),
          color: palette[lIdx % palette.length],
        })),
      });
    },
    [xLabels, yLabels, datasets, legends]
  );

  const handleCloseChart = useCallback(() => setChartInfo(null), []);

  // JS-based frozen first column: on horizontal scroll, translate
  // the corner cell and all row header cells to stay pinned at left.
  // This is immune to CSS cascade issues that break position:sticky.
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let rafId = 0;
    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const left = container.scrollLeft;
        const cells = container.querySelectorAll(
          ".grid-report-row-header, .grid-report-corner-cell"
        );
        for (let i = 0; i < cells.length; i++) {
          (cells[i] as HTMLElement).style.transform = `translateX(${left}px)`;
        }
      });
    };

    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <Card className="grid-report-card gr-results-card">
      <div className="gr-toolbar-accent" />
      <Card.Body style={{ padding: 0 }}>
        {/* Legend indicator + AI interpret button */}
        <div className="grid-report-legend-bar">
          <div className="gr-legend-list">
            {legendCount > 1 &&
              legends.map((legend, idx) => (
                <span key={idx} className="gr-legend-chip">
                  <span
                    className="gr-legend-dot"
                    style={{ backgroundColor: `var(--chart-${(idx % 6) + 1})` }}
                  />
                  {legend}
                </span>
              ))}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {onExport && (
              <button
                type="button"
                onClick={onExport}
                disabled={exporting}
                className="grid-report-export-btn"
                title="Export the full report to Excel"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {exporting ? "Exporting…" : "Export to Excel"}
              </button>
            )}
            {onInterpret && (
              <button
                type="button"
                onClick={onInterpret}
                className="grid-report-interpret-btn"
              >
                &#10022; Interpret with AI
              </button>
            )}
          </div>
        </div>

        <div className="grid-report-scroll-container" ref={scrollRef}>
          <table className="grid-report-table">
            <thead>
              <tr>
                <th className="grid-report-corner-cell" />
                {xLabels.map((xLabel) => (
                  <th key={String(xLabel.id)} className="grid-report-col-header">
                    {xLabel.name}
                  </th>
                ))}
                <th className="grid-report-col-header grid-report-total-header">
                  TOTAL
                </th>
              </tr>
            </thead>
            <tbody>
              {yLabels.map((yLabel, yIdx) => (
                <tr key={String(yLabel.id)}>
                  <th scope="row" className="grid-report-row-header">{yLabel.name}</th>
                  {xLabels.map((_xLabel, xIdx) => (
                    <GridCell
                      key={xIdx}
                      values={getCellValues(yIdx, xIdx)}
                      legends={legends}
                      onClick={(legendIndex) => handleCellClick(xIdx, yIdx, legendIndex)}
                    />
                  ))}
                  <GridCell
                    values={getRowTotalValues(yIdx)}
                    legends={legends}
                    isTotal
                    onChartClick={() => handleRowChart(yIdx)}
                  />
                </tr>
              ))}

              {/* Page Total row — only shown when paginated */}
              {isPaginated && (
                <tr className="grid-report-page-total-row">
                  <th scope="row" className="grid-report-row-header" style={{ fontWeight: 600, fontStyle: "italic" }}>
                    Page Total
                  </th>
                  {xLabels.map((_xLabel, xIdx) => (
                    <GridCell
                      key={xIdx}
                      values={pageColSubtotals.map((legend) => legend[xIdx] ?? 0)}
                      legends={legends}
                      isTotal
                    />
                  ))}
                  <GridCell
                    values={pageGrandSubtotal}
                    legends={legends}
                    isTotal
                  />
                </tr>
              )}

              {/* Grand Total row */}
              <tr className="grid-report-total-row">
                <th scope="row" className="grid-report-row-header" style={{ fontWeight: 700 }}>
                  {isPaginated ? "Grand Total" : "TOTAL"}
                </th>
                {xLabels.map((_xLabel, xIdx) => (
                  <GridCell
                    key={xIdx}
                    values={getColTotalValues(xIdx)}
                    legends={legends}
                    isTotal
                    onChartClick={() => handleColChart(xIdx)}
                  />
                ))}
                <GridCell
                  values={totals.grandTotal}
                  legends={legends}
                  isTotal
                />
              </tr>
            </tbody>
          </table>
        </div>

        <PaginationControls
          pagination={pagination}
          onPageChange={onPageChange}
        />
      </Card.Body>

      <ChartModal
        show={!!chartInfo}
        onHide={handleCloseChart}
        data={chartInfo}
      />
    </Card>
  );
};

export default PivotGrid;
